import { useState, useCallback } from 'react';
import { dispatchPersist } from '../constants/storageKeys';

function safeParse(json, fallback) {
  try {
    return JSON.parse(json);
  } catch {
    return fallback;
  }
}

/**
 * @template T
 * @param {string} storageKey
 * @param {T} initialValue
 * @returns {[T, (updater: T | ((prev: T) => T)) => void]}
 */
export function usePersistentState(storageKey, initialValue) {
  const [state, setState] = useState(() => {
    if (typeof window === 'undefined') return initialValue;
    const raw = window.localStorage.getItem(storageKey);
    if (raw == null) return initialValue;
    return safeParse(raw, initialValue);
  });

  const setPersistent = useCallback(
    (updater) => {
      setState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (JSON.stringify(prev) === JSON.stringify(next)) {
          return prev;
        }
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(next));
          dispatchPersist(storageKey);
        } catch (_) {
          /* quota or private mode */
        }
        return next;
      });
    },
    [storageKey]
  );

  return [state, setPersistent];
}
